import React, { ReactComponentElement, createRef, useRef } from 'react'

import {
  Button,
  Container,
  Content,
  ContentScroll,
  ModalComponent,
  SubTitle,
  Title,
} from './styles'
import useIsMobile from '../../../hooks/useIsMobile'
import FileUpload from './FileUpload'
import ButtonComp from '../../../components/Button'
import { api } from '../../../services/api'
import useToast from '../../../hooks/useToast'
import { useDirectory } from '../../../store/directory' 
import { Column, ContentCenter } from '../../commons'
import SelectInput from '../../Input/SelectInput'
import { useForm } from 'react-hook-form'
import { DocumentType } from '../../../services/documentTypes.service'
import { useNavigate } from 'react-router-dom'
import { FaFolder } from 'react-icons/fa6'
import { ResponseError } from '../../../validators/utils.interface'
import axios from 'axios'
import { io } from 'socket.io-client'

const { REACT_APP_SOCKET_URL = '' } = process.env

export interface ModalHandle {
  openModal: () => void
  closeModal: () => void
}

export interface ModalProps {
  title?: string
  maxWidth?: number
  refetch: () => void
  onOpenModal?: () => void
  children?: ReactComponentElement<any>
}

export interface FileInterface {
  _id: any
  file: File
  name: string
  size: number
}

export interface SelectDocument {
  control: any
  menuPortalTarget?: any
  menuPosition?: any
  small?: boolean
  disabled?: boolean
}

export const SelectDocumentType = ({
  control,
  menuPortalTarget,
  menuPosition,
  small,
  disabled,
}: SelectDocument) => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [documentsTypes, setDocumentsTypes] = React.useState([])

  const toast = useToast()
  const getDocumentsTypes = async () => {
    try {
      setIsLoading(true)

      const { data: response } = await api.get(`/documentTypes`, {
        params: {
          perPage: 10000,
          sort: "name"
        },
      })

      setDocumentsTypes(response.data)
    } catch (error) {
      const typedError = error as ResponseError
      if (axios.isAxiosError(error)) {
        //console.log(typedError)
        toast.errorToast(typedError?.response?.data?.message)
      } else {
        toast.errorToast('Erro ao deletar o registro !')
      }
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getDocumentsTypes()
  }, [])

  return (
    <>
      <SelectInput
        label='Tipo de arquivo'
        name='documentType'
        disabled={disabled}
        small={small}
        menuPosition={menuPosition}
        menuPortalTarget={menuPortalTarget}
        isLoading={isLoading}
        control={control}
        isClearable
        options={documentsTypes.map((doc: DocumentType) => ({
          label: doc.name,
          value: doc._id,
        }))}
      />
    </>
  )
}

const ModalUpload: React.ForwardRefRenderFunction<ModalHandle, ModalProps> = (
  { title, onOpenModal, maxWidth, children, refetch },
  ref,
) => {
  const isMobile = useIsMobile()
  const directory = useDirectory((state) => state.directory)
  const [isHovered, setIsHovered] = React.useState(false)
  const [socketObj, setSocketObj] = React.useState<any>()

  const [isLoading, setisLoading] = React.useState(false)
  const [files, setfiles] = React.useState<FileInterface[]>([])

  const fileRefs: any = useRef([])

  const nav = useNavigate()
  const toast = useToast()

  const refFile: any = useRef()

  const [open, setOpen] = React.useState(false)

  const {
    control,
    getValues,
    formState: { errors },
  } = useForm<{ documentType: string }>({})

  React.useImperativeHandle(ref, () => ({
    openModal() {
      setOpen(true)
      setfiles([])
      refFile.current = []
    },
    closeModal() {
      setOpen(false)
    },
  }))

  const handleFileInput = (e: any) => {
    const novosArquivos: any = Array.from(e.target.files)
    const filesNew = [...files]

    novosArquivos.forEach((file: any) => {
      const socketId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const payload: FileInterface = {
        _id: socketId,
        file: file,
        name: file.name,
        size: file.size,
      }

      if (file.type === 'application/pdf') {
        fileRefs.current.push(createRef())
        filesNew.push(payload)
      } else {
        toast.errorToast('Somente é aceito arquivo em PDF')
      }
    })

    setfiles(filesNew)
  }

  const handleDrop = (e: any) => {
    e.preventDefault()

    const novosArquivos: any = Array.from(e.dataTransfer.files)
    const filesNew = [...files]

    novosArquivos.forEach((file: any) => {
      const socketId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const payload: FileInterface = {
        _id: socketId,
        file: file,
        name: file.name,
        size: file.size,
      }

      if (file.type === 'application/pdf') {
        fileRefs.current.push(createRef())
        filesNew.push(payload)
      } else {
        toast.errorToast('Somente é aceito arquivo em PDF')
      }
    })

    setfiles(filesNew)
  }

  const afterOpenModal = () => {
    if (onOpenModal) {
      onOpenModal()
    }
  }

  const handleUpload = async () => {
    try {
      setisLoading(true)
      let dirs = [...directory]
      let lastDir = dirs.pop()
      let erros = []
      let errosFile = []
      let index = 0
      let count = 0
      for await (const arquivo of files) {
        console.log(`${index + 1} de ${files.length} - ${(((index+1)*100)/files.length).toFixed(2)}%`)
        console.log(`Erros: ${errosFile.length}`)
        let started = fileRefs.current[index].current.isStarted
        let doneUpload = fileRefs.current[index].current.doneUpload
        if (!started && !doneUpload) {
          const formControlData = getValues()

          const formData = new FormData()

          formData.append('document', arquivo.file)
          formData.append('socketId', arquivo._id)
          
          if (lastDir?.url !== '/') {
            formData.append('url', String(lastDir?.url))
          }
          
          if (formControlData.documentType) {
            formData.append('documentType', formControlData.documentType)
          }
          
          // Se já existir documento com o mesmo nome na pasta, pergunta antes de substituir
          const folderId = lastDir?.url && lastDir.url !== '/' ? String(lastDir.url) : '/'
          let replaceConfirmed = false
          try {
            const { data: sameName } = await api.get('/documents/searchDocument', {
              params: { name: arquivo.file.name.slice(0, -4), folder: folderId },
            })
            const existing = Array.isArray(sameName) ? sameName.filter((d: any) => !d.isFolder) : []

            if (existing.length > 0) {
              replaceConfirmed = window.confirm(
                `Já existe um documento chamado "${arquivo.file.name}" nesta pasta.

Deseja substituí-lo? O documento atual será excluído e o novo arquivo enviado no lugar.`,
              )

              if (!replaceConfirmed) {
                fileRefs.current[index].current.setError()
                errosFile.push(arquivo)
                toast.errorToast(`Envio de "${arquivo.file.name}" cancelado: já existe um documento com esse nome.`)
                index++
                continue
              }
            }
          } catch (error) {
            // Se a verificação falhar, o backend ainda recusa duplicados (409) sem substituir.
          }

          if (replaceConfirmed) {
            formData.append('replace', 'true')
          }

          fileRefs.current[index].current.setStarted()
          fileRefs.current[index].current.setSocketId(arquivo._id)

          try {
            const response = await api.post('/documents', formData, {
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total);
                  fileRefs.current[index].current.setProgress(percentCompleted);
                }
              }
            })
            
            fileRefs.current[index].current.setProgress(100)
            fileRefs.current[index].current.setUploadedId(response.data._id)
          } catch (error) {
            const typedError = error as ResponseError
            
            if (axios.isAxiosError(error)) {
              toast.errorToast(typedError?.response?.data?.message)
            } else {
              toast.errorToast('Erro ao efetuar o login !')
            }
            fileRefs.current[index].current.setError()
            throw error
          }
          //if (count > 5){
          let count = 0
          let dest = parseInt((arquivo.size/100000).toString())
              if (dest<15){
                dest = 15
              }
            while(!fileRefs.current[index].current.doneUpload){    
              count++
              
              if(count == dest){
              //if(count == 10){
                //erros.push(fileRefs.current[index])
                fileRefs.current[index].current.setError()
                errosFile.push(arquivo)
                
                break                
              }
              
              await sleep(100)
            }
            //fileRefs.current[index].current.doneUpload = false
          //}
          if(fileRefs.current[index].current.setError.setError){
            //erros.push(fileRefs.current[index])
          } 

          if(erros.length >0){
            
          }        
          index++
          
        }
        
      }
      
      let timer = setInterval(() => {
        let isFinished = true
        fileRefs.current.forEach((ref: any) => {
          if (!ref.current?.doneUpload) {
            isFinished = false
          }
        })

        if (isFinished) {
          clearInterval(timer)
          if (refetch) refetch()
          setisLoading(false)
        }
      }, 10000)
      if(erros.length==0){
        setisLoading(false)
        toast.successToast(`Arquivos Enviados com sucesso!`)
        setfiles(errosFile)
      } else {
        setisLoading(false)
        toast.errorToast(`Falha no envio de arquivos!`)
        for(let i = 0; i < erros.length; i++){
          //console.log(erros[i])
          //if(erros[i].current.doneUpload == true || Number.isNaN(erros[i].current.name)){
            //erros.splice(i,1)
            //errosFile.splice(i,1)
          //}
        }
        setfiles(errosFile)
      }
      
    } catch (error) {
      console.log(error)
      setisLoading(false)
      toast.errorToast('Ocorreu um erro ao enviar os arquivos!')
    }
  }

  const onDelete = async (_id: string, index: number) => {
    try {
      fileRefs.current[index].current.setIsloading(true)
      await api.delete(`/documents/${_id}`)

      fileRefs.current[index].current.setIsloading(false)
      onRemove(index)
      toast.successToast('Arquivo deletado com sucesso!')
      if (refetch) refetch()
    } catch (error) {}
  }

  const onRemove = (index: number) => {
    const novosArquivos: any = [...files]
    novosArquivos.splice(index, 1)

    fileRefs.current.splice(index, 1)

    setfiles(novosArquivos)
  }

  const handleDragEnter = (e: any) => {
    e.preventDefault()

    setIsHovered(true)
  }

  const handleDragLeave = () => {
    setIsHovered(false)
  }

  React.useEffect(() => {
    if (open && REACT_APP_SOCKET_URL) {
      const socket = io(REACT_APP_SOCKET_URL, {
        transports: ['websocket'],
        secure: true,
      })
      setSocketObj(socket)

      return () => {
        socket.close()
        setSocketObj(undefined)
      }
    } else if (socketObj && !open) {
      socketObj.close()
      setSocketObj(undefined)
    }
  }, [open])

  return (
    <ModalComponent
      isOpen={open}
      onAfterOpen={afterOpenModal}
      onRequestClose={() => setOpen(false)}
      contentLabel='Example Modal'
    >
      <Container>
        <Content
          isHovered={isHovered}
          maxWidth={maxWidth}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={(e) => e.preventDefault()}
        >
          <FaFolder size={55} />
          <div>
            <Title>Upload Arquivos</Title>
            <SubTitle>Formatos em PDF</SubTitle>
          </div>
          <Button onClick={() => refFile?.current.click()}>Procurar no Computador</Button>
          <div>
            <Title>Ou arraste os arquivos aqui</Title>
          </div>
        </Content>
        <input ref={refFile} hidden type='file' onChange={handleFileInput} multiple />

        <Column style={{ gap: 15 }}>
          <SelectDocumentType control={control} />
          <ContentCenter style={{ marginBottom: 25 }}>
            <ButtonComp
              loading={isLoading}
              disabled={!files.length}
              color='primary'
              onClick={handleUpload}
              variantType='block'
              fullWidth
              label='Enviar'
            />
          </ContentCenter>
        </Column>
      
        <ContentScroll>
          {files.map((file, index) => (
            <FileUpload
              socketObj={socketObj}
              onShow={(_id) => nav(`/documents/${_id}/download2`)}
              onRemove={onRemove}
              onDelete={onDelete}
              key={file._id}
              index={index}
              item={file}
              ref={fileRefs.current[index]}
            />
          ))}
        </ContentScroll>
      </Container>
    </ModalComponent>
  )
}
///document/${_id}
function sleep(n : any) {
  return new Promise(function(resolve){
      setTimeout(resolve,n*10);
  });
}

export default React.forwardRef(ModalUpload)
